import { initializeApp, getApps } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { getAuth } from 'firebase/auth';
import { initializeFirestore } from 'firebase/firestore';
import { getAnalytics, Analytics } from 'firebase/analytics';
import { logger } from '@/utils/logger';
import { isProductionHost } from '@/utils/productionHost';
import { getFirebaseConfig, assertValidFirebaseConfig, shouldUseFirebaseEmulators } from '@/utils/firebase/environment';
import {
  connectFirebaseEmulators,
  createFunctions,
  getFirestoreEmulatorSettings,
} from '@/utils/firebase/emulator';

const config = getFirebaseConfig();

logger.debug('Firebase', 'Configuration check', {
  apiKey: config.apiKey ? 'Set' : 'Missing',
  authDomain: config.authDomain ? 'Set' : 'Missing',
  projectId: config.projectId ? 'Set' : 'Missing',
  appId: config.appId ? 'Set' : 'Missing',
  useEmulator: config.useEmulator
});

assertValidFirebaseConfig();

const app = getApps().length === 0 ? initializeApp(config) : getApps()[0];

// ── Firebase App Check (anti-scraping / anti-bot) ────────────────────────────
// INERT until VITE_RECAPTCHA_SITE_KEY is set, so committing this is a no-op until
// the reCAPTCHA Enterprise key + console registration are in place. It runs in
// MONITOR mode — it only sends attestation tokens; NOTHING is enforced until we
// flip enforcement in the Firebase console + `enforceAppCheck: true` on the
// callables. Skipped against the emulators.
//   - VITE_RECAPTCHA_SITE_KEY   : reCAPTCHA Enterprise *site* key (public — safe in client)
//   - VITE_APPCHECK_DEBUG_TOKEN : optional, for localhost/dev (register it under
//                                 App Check → Apps → Manage debug tokens)
if (typeof window !== 'undefined') {
  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined;
  if (siteKey && !shouldUseFirebaseEmulators()) {
    try {
      const debugToken = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN as string | undefined;
      if (debugToken) {
        (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string }).FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken;
      }
      // Prerendered pages (SEO #203) can serialize App Check's rendered
      // reCAPTCHA into the static HTML. On boot the SDK then tries to render
      // into its own non-empty container and reCAPTCHA throws "placeholder
      // element must be empty"; token fetches time out and every anonymous
      // callable hangs (the Jul 9-11 captureLead outage). Scrub the snapshot's
      // widget before initializing so the SDK renders fresh.
      document.getElementById('fire_app_check_[DEFAULT]')?.remove();
      document.querySelectorAll('.grecaptcha-badge, script[src*="gstatic.com/recaptcha"], script[src*="recaptcha/enterprise"]')
        .forEach((n) => n.remove());
      initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(siteKey),
        isTokenAutoRefreshEnabled: true,
      });
      logger.debug('Firebase', 'App Check initialized (reCAPTCHA Enterprise, monitor mode)');
    } catch (error) {
      // Non-fatal: never let App Check setup break app boot.
      logger.error('Firebase', 'Failed to initialize App Check', { error });
    }
  }
}

export const auth = getAuth(app);
// ignoreUndefinedProperties: Firestore throws on `undefined` field values by
// default. Optional fields left blank in the UI (e.g. an exam result with no
// score/feedback) were serializing to `undefined` and hard-failing the write
// ("Failed to save your exam result"). Dropping undefined keys is the idiomatic
// web-SDK setting and prevents this whole class of write failure app-wide.
// getFirestoreEmulatorSettings(): {} for production AND for the local http emulator
// suite (which keeps using connectFirestoreEmulator), but { host, ssl: true } when
// VITE_FIREBASE_EMULATOR_HOST is an https:// origin. connectFirestoreEmulator()
// hardcodes ssl:false and therefore cannot reach staging's TLS ingress at all, so the
// host has to be set here, at construction.
export const db = initializeFirestore(app, {
  ignoreUndefinedProperties: true,
  ...getFirestoreEmulatorSettings(),
});
// createFunctions(), never getFunctions() directly: it applies
// VITE_FUNCTIONS_BASE_URL so callables resolve against staging's custom domain.
export const functions = createFunctions(app);

// Only report to production GA4 from the real production domain. Without this
// guard, `npm run dev` (localhost) and Vercel preview deploys fire into the
// production Analytics property — which polluted it badly: 192 of 198
// error_occurred events (and the entire "/quiz useAuth" error storm) came from
// localhost vs just 6 from real users. Gate at the source.
let analytics: Analytics | null = null;
if (isProductionHost()) {
  if (config.measurementId && config.measurementId !== 'G-ABCDEF123') {
    try {
      analytics = getAnalytics(app);
      logger.debug('Firebase', 'Analytics initialized successfully');
    } catch (error) {
      logger.error('Firebase', 'Failed to initialize analytics', { error });
    }
  } else {
    logger.warn('Firebase', 'Analytics not initialized: Invalid or missing measurementId');
  }
} else {
  logger.debug('Firebase', 'Analytics disabled off-production host', {
    host: typeof window !== 'undefined' ? window.location.hostname : 'ssr',
  });
}
export { analytics };

if (shouldUseFirebaseEmulators()) {
  logger.debug('Firebase', 'Attempting to connect to Firebase emulators');
  connectFirebaseEmulators({ auth, firestore: db, functions });
} else {
  logger.debug('Firebase', 'Using production Firebase services');
}

export default app;
